import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  MIN_OBSTACLE_GAP_CM,
  interiorMask,
  withinInterior,
} from "./tableArrangement";

// The reservation plan and the room designer describe the same floor, so they
// share one coordinate system: real centimetres, with the canvas drawn at the
// room's true aspect ratio. Positions still round-trip through the legacy
// 750x400 pixel space, because that is what reservation layout overrides store.
const LEGACY_CANVAS_WIDTH = 750;
const LEGACY_CANVAS_HEIGHT = 400;

const DEFAULT_TABLE_CM = 80;

// How far a press may travel before it counts as dragging a table rather than
// clicking it. Four pixels is below the wobble of a deliberate click.
const DRAG_THRESHOLD_PX = 4;

// One press of the zoom buttons.
const ZOOM_STEP = 0.15;

// A table with no booking has no group, so nothing pulls it.
const NO_MATES = new Set();

// Snap thresholds, in centimetres. These were pixel values, which at a typical
// room scale meant tables snapping from half a metre away.
const SNAP_DISTANCE = 20;
const CENTER_SNAP_DISTANCE = 9;
// Once an axis has snapped it holds until the pointer pulls this far past the
// snapped value, which stops the snap flickering on and off while the hand
// hovers at the threshold.
const SNAP_RELEASE_DISTANCE = 20;
// ...but a held snap gives way the moment a different alignment is clearly
// nearer. Holding on regardless is what made it hard to move a table from a
// side join to a top join: the axis stayed clamped beside the neighbour, so the
// top alignment could never come into range.
const SNAP_SWITCH_MARGIN = 3;
const SNAP_OVERLAP_TOLERANCE = 22;

// Gap the arrangement engine leaves between two tables it pushes together.
// Mirrored from tableArrangement so the renderer knows what counts as welded.
const JOIN_GAP_CM = 2;

// Below these shares of the canvas there is no room for a table's number or an
// element's caption without it spilling over the shape.
const LABEL_MIN_PERCENT = 2.6;
const CAPTION_MIN_PERCENT = 3.2;

const C = {
  orange: "#ee754c",
  orangeDark: "#cf5b36",
  ink: "#3f3537",
  text: "#61585a",
  line: "#e9e3e1",
  sage: "#99b0ad",
  sageDark: "#6d8b87",
};

const PlanFrame = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
`;

const PlanScroll = styled.div`
  flex: 1;
  min-height: 180px;
  overflow: auto;
  overscroll-behavior: none;
  /* Drag the floor to pan. Two fingers pinch to zoom, which the pinch handler
     takes over, so the browser is only asked to handle one-finger panning. */
  cursor: ${({ $panning }) => ($panning ? "grabbing" : "grab")};
  touch-action: pan-x pan-y;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: #f3f6f5;

  /* Opt-in: a landscape room in a portrait window leaves height over, and
     letterboxing it above and below reads better than a plan pinned to the top
     with a grey void under it. Auto margins on the child do the centring
     rather than justify-content, which would put the top of a zoomed-in plan
     out of scroll reach. */
  ${({ $center }) => $center && "display: flex; flex-direction: column;"}

  scrollbar-color: ${C.sage} transparent;
  scrollbar-width: thin;
`;

const ZoomSurface = styled.div`
  width: ${({ $zoom }) => `${$zoom * 100}%`};
  min-width: min(320px, 100%);
  margin: ${({ $center }) => ($center ? "auto" : "0 auto")};
  flex-shrink: 0;
`;

const PlanCanvas = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $ratio }) => $ratio};
  overflow: hidden;
  border: 0;
  border-radius: 0;
  background-color: #fff;
  background-image:
    linear-gradient(to right, rgba(153, 176, 173, 0.16) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(153, 176, 173, 0.16) 1px, transparent 1px);
  background-size: ${({ $gridX, $gridY }) => `${$gridX}% ${$gridY}%`};
`;

const FloorPlanImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: ${({ $opacity }) => $opacity};
  pointer-events: none;
  user-select: none;
`;

/* --------------------------------------------------------------- elements */

const SLAB_TYPES = new Set(["wall", "window", "door"]);

const OBJECT_ICONS = {
  kitchen: "\u{1F373}",
  wc: "\u{1F6BB}",
  bar: "\u{1F378}",
  door: "\u{1F6AA}",
  wall: "▬",
  window: "▤",
  stairs: "⇅",
  reception: "\u{1F514}",
  plant: "\u{1F33F}",
  custom: "◇",
  image: "\u{1F5BC}",
};

const ObjectShape = styled.div`
  position: absolute;
  left: ${({ $left }) => $left}%;
  top: ${({ $top }) => $top}%;
  width: ${({ $width }) => $width}%;
  height: ${({ $height }) => $height}%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 3%;
  overflow: hidden;
  border-radius: ${({ $type }) => {
    if ($type === "wall") return "1px";
    if (SLAB_TYPES.has($type)) return "2px";
    return "10px";
  }};
  background-color: ${({ $color }) => $color || C.sage};
  background-image: linear-gradient(
    158deg,
    rgba(255, 255, 255, 0.3),
    rgba(255, 255, 255, 0) 52%,
    rgba(67, 48, 52, 0.14)
  );
  color: #fff;
  /* Elements are context on this page rather than the subject, so they sit
     back a little and never intercept a click meant for a table. */
  opacity: ${({ $opacity }) => $opacity * 0.75};
  box-shadow:
    inset 0 0 0 1px rgba(67, 48, 52, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  pointer-events: none;
  transform: rotate(${({ $rotation }) => $rotation}deg);
  transform-origin: center;
  z-index: 1;

  span:first-child {
    font-size: clamp(0.55rem, 1.3vw, 1.2rem);
    line-height: 1;
    filter: drop-shadow(0 1px 1px rgba(67, 48, 52, 0.3));
  }

  span:last-child {
    max-width: 92%;
    overflow: hidden;
    font-size: clamp(0.42rem, 0.74vw, 0.64rem);
    font-weight: 850;
    letter-spacing: 0.02em;
    text-overflow: ellipsis;
    text-shadow: 0 1px 2px rgba(46, 33, 36, 0.45);
    white-space: nowrap;
  }
`;

/* ----------------------------------------------------------------- tables */

const TableNode = styled.button`
  position: absolute;
  left: ${({ $left }) => $left}%;
  top: ${({ $top }) => $top}%;
  width: ${({ $width }) => $width}%;
  height: ${({ $height }) => $height}%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: ${({ $movable }) => ($movable ? "move" : "pointer")};
  transform: rotate(${({ $rotation }) => $rotation}deg);
  transform-origin: center;
  touch-action: none;
  z-index: ${({ $dragging }) => ($dragging ? 20 : 4)};

  /* Widens the pointer target without changing the drawn size, so a table in a
     large room stays clickable when it is only a few pixels across. */
  &::before {
    content: "";
    position: absolute;
    inset: -6px;
    border-radius: 8px;
  }
`;

const TableBody = styled.div`
  position: absolute;
  inset: 0;
  filter: drop-shadow(0 1px 2px rgba(67, 48, 52, 0.16));
`;

const surfaceFor = (state) => {
  if (state === "selected") {
    return `linear-gradient(155deg, ${C.orange}, ${C.orangeDark})`;
  }
  if (state === "occupied") {
    return `linear-gradient(155deg, ${C.sage}, ${C.sageDark})`;
  }
  // Booked, but not yet: pale sage so it reads as "this one is spoken for"
  // without competing with the tables actually in use.
  if (state === "upcoming") {
    return "linear-gradient(155deg, #f4f8f7, #dfe9e6)";
  }
  return "linear-gradient(155deg, #ffffff, #ece3d8)";
};

/**
 * Where a table's top sits inside its footprint, per axis.
 *
 * The footprint includes the chair allowance, so the drawn top is inset on all
 * four sides. Welding a side used to set that side's inset to zero, which made
 * the top grow by a whole chair's width the moment it snapped - the table
 * appeared to swell to fit its neighbour.
 *
 * Instead the top SLIDES into the space the removed chairs left: the welded
 * side goes to the edge and the opposite side takes up the slack, so the top
 * keeps exactly its size and the two surfaces still meet. Only a table welded
 * on both sides of an axis has to stretch, and there it genuinely is bridging
 * the middle of a row.
 */
const surfaceInsets = (joinedStart, joinedEnd, inset) => {
  if (joinedStart && joinedEnd) return ["0", "0"];
  if (joinedStart) return ["0", `${inset * 2}%`];
  if (joinedEnd) return [`${inset * 2}%`, "0"];
  return [`${inset}%`, `${inset}%`];
};

const TableSurface = styled.div`
  position: absolute;
  top: ${({ $round, $joined }) =>
    surfaceInsets($joined.top, $joined.bottom, $round ? 17 : 18)[0]};
  bottom: ${({ $round, $joined }) =>
    surfaceInsets($joined.top, $joined.bottom, $round ? 17 : 18)[1]};
  left: ${({ $round, $joined }) =>
    surfaceInsets($joined.left, $joined.right, $round ? 17 : 16)[0]};
  right: ${({ $round, $joined }) =>
    surfaceInsets($joined.left, $joined.right, $round ? 17 : 16)[1]};
  display: grid;
  place-items: center;
  padding: 0 6%;
  overflow: hidden;
  border-radius: ${({ $round, $joined }) => {
    const welded =
      $joined.top || $joined.bottom || $joined.left || $joined.right;
    if (welded) {
      // Keep rounding only on corners whose both sides are free.
      const r = $round ? "24%" : "14%";
      const tl = $joined.top || $joined.left ? "0" : r;
      const tr = $joined.top || $joined.right ? "0" : r;
      const br = $joined.bottom || $joined.right ? "0" : r;
      const bl = $joined.bottom || $joined.left ? "0" : r;
      return `${tl} ${tr} ${br} ${bl}`;
    }
    return $round ? "50%" : "14%";
  }};
  background: ${({ $state }) => surfaceFor($state)};
  color: ${({ $state }) => {
    if ($state === "free") return C.ink;
    if ($state === "upcoming") return C.sageDark;
    return "#fff";
  }};
  /* Dashed, so a table held for later is distinguishable from one in use even
     at a glance or in monochrome. */
  outline: ${({ $state }) =>
    $state === "upcoming" ? `1px dashed ${C.sage}` : "none"};
  outline-offset: -2px;
  font-size: clamp(0.5rem, 1vw, 0.78rem);
  font-weight: 850;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* The ring is drawn edge by edge so no line is painted where two tables meet;
     a full inset ring would leave a visible seam through the joined surface. */
  ${({ $state, $joined }) => {
    const line =
      $state === "free" ? "rgba(67, 48, 52, 0.16)" : "rgba(255, 255, 255, 0.45)";
    const edges = [];
    if (!$joined.top) edges.push(`inset 0 1px 0 0 ${line}`);
    if (!$joined.bottom) edges.push(`inset 0 -1px 0 0 ${line}`);
    if (!$joined.left) edges.push(`inset 1px 0 0 0 ${line}`);
    if (!$joined.right) edges.push(`inset -1px 0 0 0 ${line}`);
    edges.push("inset 0 1px 0 rgba(255, 255, 255, 0.7)");
    return `box-shadow: ${edges.join(", ")};`;
  }}
`;

const Chair = styled.span`
  position: absolute;
  left: ${({ $left }) => $left}%;
  top: ${({ $top }) => $top}%;
  width: clamp(4px, 15%, 13px);
  height: clamp(4px, 15%, 13px);
  border-radius: 45% 45% 22% 22%;
  background: ${({ $state }) => {
    if ($state === "selected") {
      return "linear-gradient(180deg, #ffb69b, #ef8963)";
    }
    if ($state === "occupied") {
      return "linear-gradient(180deg, #c2d3d0, #91a9a5)";
    }
    if ($state === "upcoming") {
      return "linear-gradient(180deg, #f0f5f3, #dbe6e3)";
    }
    return "linear-gradient(180deg, #fdf9f5, #ddd2c6)";
  }};
  box-shadow:
    inset 0 0 0 1px rgba(67, 48, 52, 0.18),
    0 1px 1px rgba(67, 48, 52, 0.12);
  transform: translate(-50%, -50%) rotate(${({ $angle }) => $angle}deg);
`;

/* A booked table carries who is sitting there, hung below the table so it does
   not cover the table number. */
const BookingTag = styled.span`
  position: absolute;
  /* Shifted so a group's single tag sits under the middle of the whole block,
     not under whichever of its tables happens to carry it. */
  left: ${({ $shift }) => `calc(50% + ${$shift || 0}%)`};
  top: calc(100% + 4px);
  z-index: 6;
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: 190px;
  padding: 2px 6px;
  border-radius: 999px;
  background: ${({ $selected, $upcoming }) => {
    if ($selected) return C.orangeDark;
    // Lighter tag for a booking that has not arrived yet, so the tables in use
    // still read first.
    if ($upcoming) return "#7d8f8b";
    return "#4b4143";
  }};
  color: #fff;
  font-size: 0.6rem;
  font-weight: 800;
  line-height: 1.35;
  pointer-events: none;
  transform: translateX(-50%);
  white-space: nowrap;

  b {
    max-width: 108px;
    overflow: hidden;
    font-weight: 850;
    text-overflow: ellipsis;
  }

  i {
    font-style: normal;
    opacity: 0.75;
  }
`;

const SnapGuide = styled.div`
  position: absolute;
  left: ${({ $left }) => $left}%;
  top: ${({ $top }) => $top}%;
  width: ${({ $width }) => $width}%;
  height: ${({ $height }) => $height}%;
  border: 1px solid rgba(238, 117, 76, 0.75);
  border-radius: 8px;
  background: rgba(238, 117, 76, 0.08);
  box-shadow: 0 0 0 3px rgba(238, 117, 76, 0.1);
  pointer-events: none;
  z-index: 15;
`;

/* ----------------------------------------------------------------- footer */

const PlanFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 2px;
`;

const FooterSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

/* One pill holding the whole zoom control, so it reads as a single instrument
   rather than four loose widgets. */
const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
  padding: 3px;
  border-radius: 999px;
  background: #f4f7f6;
  box-shadow: inset 0 0 0 1px rgba(153, 176, 173, 0.28);
  color: ${C.text};
  font-size: 0.78rem;
`;

const ZoomButton = styled.button`
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: ${C.ink};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background-color 0.14s ease,
    color 0.14s ease;

  &:hover:not(:disabled) {
    background: #ffffff;
    color: ${C.orange};
  }

  &:active:not(:disabled) {
    transform: scale(0.94);
  }

  &:disabled {
    cursor: default;
    opacity: 0.35;
  }
`;

const ZoomGlyph = styled.span`
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
`;

/* The track is drawn by the wrapper: a slim rail with an orange fill, with the
   real input laid over it, transparent, so it keeps its keyboard and drag
   behaviour while looking like part of the pill. */
const ZoomTrack = styled.div`
  position: relative;
  width: 104px;
  height: 28px;
  display: flex;
  align-items: center;
  flex: 0 0 auto;

  &::before {
    content: "";
    position: absolute;
    left: 4px;
    right: 4px;
    height: 4px;
    border-radius: 999px;
    background: rgba(153, 176, 173, 0.42);
  }

  /* Below the pill's comfortable width the rail goes and the buttons remain -
     on a phone the plan is pinched, not dragged along a 100px slider. */
  @media (max-width: 640px) {
    display: none;
  }
`;

const ZoomFill = styled.div`
  position: absolute;
  left: 4px;
  height: 4px;
  max-width: calc(100% - 8px);
  border-radius: 999px;
  background: ${C.orange};
  pointer-events: none;
`;

const ZoomRange = styled.input`
  position: relative;
  width: 100%;
  margin: 0;
  background: transparent;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;

  &::-webkit-slider-runnable-track {
    height: 4px;
    background: transparent;
  }

  &::-moz-range-track {
    height: 4px;
    background: transparent;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    margin-top: -5px;
    border: 0;
    border-radius: 50%;
    background: #ffffff;
    box-shadow:
      0 0 0 2px ${C.orange},
      0 1px 3px rgba(75, 51, 53, 0.28);
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border: 0;
    border-radius: 50%;
    background: #ffffff;
    box-shadow:
      0 0 0 2px ${C.orange},
      0 1px 3px rgba(75, 51, 53, 0.28);
  }

  &:focus-visible {
    outline: none;
  }

  &:focus-visible::-webkit-slider-thumb {
    box-shadow:
      0 0 0 2px ${C.orange},
      0 0 0 5px rgba(238, 117, 76, 0.25);
  }
`;

const ZoomPercent = styled.button`
  min-width: 46px;
  height: 28px;
  padding: 0 8px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: ${C.ink};
  font-size: 0.74rem;
  font-weight: 750;
  font-variant-numeric: tabular-nums;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.14s ease;

  &:hover {
    background: #ffffff;
    color: ${C.orange};
  }
`;

const EmptyState = styled.div`
  padding: 22px;
  border: 1px dashed #e3d8cf;
  border-radius: 12px;
  background: #fffaf4;
  color: #7a6d68;
  font-weight: 650;
  text-align: center;
`;

const num = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const positive = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const isPlacedFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

// Chair layout for a table, aware of which sides are welded to a neighbour.
//
// When two tables of the same booking are pushed together they become one
// surface: the chairs that would sit in the join are removed and their covers
// are redistributed to the sides that are still open, which is what actually
// happens when staff push two tables together.
const chairPositions = (seatCount, joined = {}) => {
  const count = clamp(Number(seatCount) || 4, 1, 12);

  const welded =
    !!joined.top || !!joined.bottom || !!joined.left || !!joined.right;

  // An ordinary table keeps the room designer's arrangement — covers along the
  // long sides only — so a table looks the same on both screens. Only a welded
  // table needs its chairs re-dealt.
  const sides = welded
    ? [
        { key: "top", open: !joined.top },
        { key: "bottom", open: !joined.bottom },
        { key: "left", open: !joined.left },
        { key: "right", open: !joined.right },
      ]
    : [
        { key: "top", open: true },
        { key: "bottom", open: true },
      ];
  const openSides = sides.filter((side) => side.open);

  // Fully enclosed: an interior table of a large block seats nobody of its own.
  if (!openSides.length) return [];

  // Deal the covers round the open sides, longest first so a run of tables
  // fills its free length before doubling up on the ends.
  const order = openSides
    .slice()
    .sort((a, b) => {
      const rank = { top: 0, bottom: 1, left: 2, right: 3 };
      return rank[a.key] - rank[b.key];
    });

  const perSide = new Map(order.map((side) => [side.key, 0]));
  for (let index = 0; index < count; index += 1) {
    const side = order[index % order.length];
    perSide.set(side.key, perSide.get(side.key) + 1);
  }

  const out = [];
  const spread = (n, index) => ((index + 1) / (n + 1)) * 64 + 18;

  for (const side of order) {
    const n = perSide.get(side.key);
    for (let index = 0; index < n; index += 1) {
      const along = spread(n, index);
      if (side.key === "top") out.push({ left: along, top: 8, angle: 0 });
      if (side.key === "bottom") out.push({ left: along, top: 92, angle: 0 });
      if (side.key === "left") out.push({ left: 8, top: along, angle: 90 });
      if (side.key === "right") out.push({ left: 92, top: along, angle: 90 });
    }
  }

  return out;
};

// Which sides of `bounds` are welded to one of `partners`, i.e. touching within
// the join gap along a shared edge.
const joinedSides = (bounds, partners) => {
  const weld = JOIN_GAP_CM + 6;
  const result = { top: false, bottom: false, left: false, right: false };

  for (const partner of partners) {
    const overlapsY = bounds.top < partner.bottom - 1 && partner.top < bounds.bottom - 1;
    const overlapsX = bounds.left < partner.right - 1 && partner.left < bounds.right - 1;

    if (overlapsY) {
      if (Math.abs(partner.right - bounds.left) <= weld) result.left = true;
      if (Math.abs(bounds.right - partner.left) <= weld) result.right = true;
    }
    if (overlapsX) {
      if (Math.abs(partner.bottom - bounds.top) <= weld) result.top = true;
      if (Math.abs(bounds.bottom - partner.top) <= weld) result.bottom = true;
    }
  }

  return result;
};

function buildAvailabilityMap(availabilityTables) {
  const map = new Map();

  (availabilityTables || []).forEach((table) => {
    if (!table?.number) return;

    map.set(String(table.number), {
      number: String(table.number),
      status: String(table.status ?? "0"),
      reservationIds: table.reservationIds || [],
      reservationDetails: table.reservationDetails || [],
    });
  });

  return map;
}

function buildLayoutMap(layoutItems) {
  const map = new Map();

  (layoutItems || []).forEach((item) => {
    const tableId = item?.table_id || item?.tableId;

    if (!tableId) return;

    map.set(String(tableId), item);
  });

  return map;
}

function isTableSelected(selectedTables, zoneName, tableId) {
  return !!selectedTables?.[zoneName]?.some(
    (table) => String(table.number) === String(tableId),
  );
}

function getTableLabel(table) {
  return table?.table_name || table?.table_number || `#${table?.id}`;
}

function getReservationInfoText(availabilityInfo, t) {
  const reservationDetails = availabilityInfo?.reservationDetails || [];

  if (reservationDetails.length > 0) {
    return reservationDetails
      .map((reservation) => {
        const timeText = reservation.estimatedtime
          ? `${reservation.time}-${reservation.estimatedtime}`
          : reservation.time;

        return `${reservation.full_name || t("ui.i18nFinalCleanup.reservation")} (#${
          reservation.id
        }) ${timeText}`;
      })
      .join(" | ");
  }

  if (!availabilityInfo?.reservationIds?.length) {
    return "";
  }

  return t("ui.i18nFinalCleanup.occupiedByReservation", { ids: availabilityInfo.reservationIds.join(", #") });
}


function rangesOverlapWithTolerance(aStart, aEnd, bStart, bEnd, tolerance) {
  return aEnd + tolerance >= bStart && bEnd + tolerance >= aStart;
}

function getRectFromTable(table) {
  const left = Number(table?.left) || 0;
  const top = Number(table?.top) || 0;
  const width = Number(table?.width) || 50;
  const height = Number(table?.height) || 50;

  return {
    left,
    top,
    width,
    height,
    right: left + width,
    bottom: top + height,
    centerX: left + width / 2,
    centerY: top + height / 2,
  };
}

function getRectFromPosition(left, top, width, height) {
  return {
    left,
    top,
    width,
    height,
    right: left + width,
    bottom: top + height,
    centerX: left + width / 2,
    centerY: top + height / 2,
  };
}

function getBestSnapPosition({
  tableId,
  proposedLeft,
  proposedTop,
  width,
  height,
  otherTables,
  // The axis values already snapped to, if any. A held snap survives until the
  // pointer pulls clearly past it, which is what makes the magnet feel solid.
  sticky = null,
  /* Only the booking's own tables pull. Another booking's table still cannot be
     overlapped, but it has no business tugging this one into alignment - and it
     certainly should not shove it away for coming close. */
  mateIds = null,
}) {
  let snappedLeft = proposedLeft;
  let snappedTop = proposedTop;

  let bestX = {
    distance: Infinity,
    value: proposedLeft,
    guide: null,
  };

  let bestY = {
    distance: Infinity,
    value: proposedTop,
    guide: null,
  };

  const movingRect = getRectFromPosition(
    proposedLeft,
    proposedTop,
    width,
    height,
  );

  (otherTables || []).forEach((otherTable) => {
    if (!otherTable?.isPlaced) return;
    if (String(otherTable.id) === String(tableId)) return;
    if (mateIds && !mateIds.has(String(otherTable.id))) return;

    const otherRect = getRectFromTable(otherTable);

    /* Overshooting into the table you are aiming at should still land the join.
       With a band of a few centimetres either way, pushing the mouse a little
       past the edge put the target out of range, the snap dropped, and the
       collision pass left the table short - which reads as being pushed away.
       Once the two footprints overlap the band opens up to the table's own
       size, so a heavy-handed push still settles on the join. */
    const overlapping =
      movingRect.left < otherRect.right &&
      otherRect.left < movingRect.right &&
      movingRect.top < otherRect.bottom &&
      otherRect.top < movingRect.bottom;

    const captureX = overlapping ? SNAP_DISTANCE + width : SNAP_DISTANCE;
    const captureY = overlapping ? SNAP_DISTANCE + height : SNAP_DISTANCE;

    /* Once the footprints overlap, only the join on the side the table came
       from is eligible. Push past the middle of a table and the far side became
       the nearer number, so the snap aimed at a position on the other side of
       it - which the collision pass then refused, and nothing snapped at all.
       That is why the join needed hitting from one exact spot. */
    const fromLeft = movingRect.centerX <= otherRect.centerX;
    const fromAbove = movingRect.centerY <= otherRect.centerY;
    const allowLeftAttach = !overlapping || !fromLeft;
    const allowRightAttach = !overlapping || fromLeft;
    const allowTopAttach = !overlapping || !fromAbove;
    const allowBottomAttach = !overlapping || fromAbove;

    const verticalOverlap = rangesOverlapWithTolerance(
      movingRect.top,
      movingRect.bottom,
      otherRect.top,
      otherRect.bottom,
      SNAP_OVERLAP_TOLERANCE,
    );

    const horizontalOverlap = rangesOverlapWithTolerance(
      movingRect.left,
      movingRect.right,
      otherRect.left,
      otherRect.right,
      SNAP_OVERLAP_TOLERANCE,
    );

    const guide = {
      left: otherRect.left,
      top: otherRect.top,
      width: otherRect.width,
      height: otherRect.height,
    };

    if (verticalOverlap) {
      const leftAttachValue = otherRect.right + JOIN_GAP_CM;
      const leftAttachDistance = Math.abs(proposedLeft - leftAttachValue);

      if (
        allowLeftAttach &&
        leftAttachDistance <= captureX &&
        leftAttachDistance < bestX.distance
      ) {
        bestX = {
          distance: leftAttachDistance,
          value: leftAttachValue,
          guide,
        };
      }

      const rightAttachValue = otherRect.left - width - JOIN_GAP_CM;
      const rightAttachDistance = Math.abs(proposedLeft - rightAttachValue);

      if (
        allowRightAttach &&
        rightAttachDistance <= captureX &&
        rightAttachDistance < bestX.distance
      ) {
        bestX = {
          distance: rightAttachDistance,
          value: rightAttachValue,
          guide,
        };
      }
    }

    if (horizontalOverlap) {
      const topAttachValue = otherRect.bottom + JOIN_GAP_CM;
      const topAttachDistance = Math.abs(proposedTop - topAttachValue);

      if (
        allowTopAttach &&
        topAttachDistance <= captureY &&
        topAttachDistance < bestY.distance
      ) {
        bestY = {
          distance: topAttachDistance,
          value: topAttachValue,
          guide,
        };
      }

      const bottomAttachValue = otherRect.top - height - JOIN_GAP_CM;
      const bottomAttachDistance = Math.abs(proposedTop - bottomAttachValue);

      if (
        allowBottomAttach &&
        bottomAttachDistance <= captureY &&
        bottomAttachDistance < bestY.distance
      ) {
        bestY = {
          distance: bottomAttachDistance,
          value: bottomAttachValue,
          guide,
        };
      }
    }

    /* Centre alignment is for a neighbour, not for any table in the room.
       Without the overlap test a table would snap its column to a table five
       metres away, which is the alignment nobody asked for and cannot see. */
    const centerXValue = otherRect.centerX - width / 2;
    const centerXDistance = Math.abs(movingRect.centerX - otherRect.centerX);

    if (
      horizontalOverlap &&
      centerXDistance <= CENTER_SNAP_DISTANCE &&
      centerXDistance < bestX.distance
    ) {
      bestX = {
        distance: centerXDistance,
        value: centerXValue,
        guide,
      };
    }

    const centerYValue = otherRect.centerY - height / 2;
    const centerYDistance = Math.abs(movingRect.centerY - otherRect.centerY);

    if (
      verticalOverlap &&
      centerYDistance <= CENTER_SNAP_DISTANCE &&
      centerYDistance < bestY.distance
    ) {
      bestY = {
        distance: centerYDistance,
        value: centerYValue,
        guide,
      };
    }
  });

  /* Hold an established snap. Re-searching from scratch every frame meant the
     nearest target could change under the hand, so the table twitched between
     two alignments instead of staying put until it was pulled away. */
  const holdsSticky = (stickyValue, proposed, freshDistance) => {
    if (stickyValue === null || stickyValue === undefined) return false;

    const stickyDistance = Math.abs(proposed - stickyValue);

    if (stickyDistance > SNAP_RELEASE_DISTANCE) return false;

    // A clearly nearer alternative wins straight away.
    return freshDistance >= stickyDistance - SNAP_SWITCH_MARGIN;
  };

  if (sticky && holdsSticky(sticky.x, proposedLeft, bestX.distance)) {
    bestX = { distance: 0, value: sticky.x, guide: sticky.xGuide || bestX.guide };
  }

  if (sticky && holdsSticky(sticky.y, proposedTop, bestY.distance)) {
    bestY = { distance: 0, value: sticky.y, guide: sticky.yGuide || bestY.guide };
  }

  let snapGuide = null;

  if (bestX.distance !== Infinity) {
    snappedLeft = bestX.value;
    snapGuide = bestX.guide;
  }

  if (bestY.distance !== Infinity) {
    snappedTop = bestY.value;
    snapGuide = bestY.guide || snapGuide;
  }

  return {
    left: snappedLeft,
    top: snappedTop,
    guide: snapGuide,
    snappedX: bestX.distance !== Infinity ? bestX.value : null,
    snappedY: bestY.distance !== Infinity ? bestY.value : null,
    guideX: bestX.guide || null,
    guideY: bestY.guide || null,
  };
}

function mergeLayoutDraftItem(items = [], nextItem) {
  if (!nextItem?.table_id && !nextItem?.tableId) return items;

  const nextTableId = String(nextItem.table_id || nextItem.tableId);
  const nextItems = [...items];

  const existingIndex = nextItems.findIndex((item) => {
    const itemTableId = item?.table_id || item?.tableId;
    return String(itemTableId) === nextTableId;
  });

  if (existingIndex >= 0) {
    nextItems[existingIndex] = {
      ...nextItems[existingIndex],
      ...nextItem,
    };
  } else {
    nextItems.push(nextItem);
  }

  return nextItems;
}

function resolvePlanImage(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("blob:")) return url;
  return `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`;
}

// Resolve one table's position and size in centimetres.
//
// Precedence follows what the user touched most recently: an unsaved drag beats
// a saved per-reservation override, which beats the room designer's own layout.
// The first two are stored in legacy pixels and are scaled up; the designer's
// values are already centimetres.
function tableGeometryCm({
  table,
  override,
  draft,
  legacyScale,
  roomWidthCm,
  roomHeightCm,
  hasRoom,
}) {
  const source = draft || override;

  if (source) {
    // Prefer the centimetre geometry when the row carries it. Reconstructing
    // it from the legacy pixels loses up to half a pixel, which is 2.5 cm in a
    // 25 m room: enough to turn a 2 cm join into a 5 cm gap or an overlap.
    const hasCm = ![null, undefined, ""].includes(source.left_cm);

    if (hasCm) {
      return {
        left: num(source.left_cm),
        top: num(source.top_cm),
        width: positive(source.width_cm, positive(table.real_width_cm, DEFAULT_TABLE_CM)),
        height: positive(source.height_cm, positive(table.real_height_cm, DEFAULT_TABLE_CM)),
        rotation: num(source.rotation, num(table.rotation)),
        positioned: true,
      };
    }

    const fallbackPx = DEFAULT_TABLE_CM * legacyScale;

    return {
      left: num(source.left) / legacyScale,
      top: num(source.top) / legacyScale,
      width: positive(source.width, fallbackPx) / legacyScale,
      height: positive(source.height, fallbackPx) / legacyScale,
      rotation: num(source.rotation, num(table.rotation)),
      positioned: true,
    };
  }

  const width = hasRoom
    ? positive(table.real_width_cm, DEFAULT_TABLE_CM)
    : positive(table.width, 50);
  const height = hasRoom
    ? positive(table.real_height_cm, DEFAULT_TABLE_CM)
    : positive(table.height, 50);

  // left_cm is null on tables saved before the room designer existed. Those
  // still carry the legacy pixel pair, which converts cleanly.
  const hasLeftCm = ![null, undefined, ""].includes(table.left_cm);
  const hasTopCm = ![null, undefined, ""].includes(table.top_cm);

  const left = !hasRoom
    ? num(table.left)
    : hasLeftCm
      ? num(table.left_cm)
      : (num(table.left) / LEGACY_CANVAS_WIDTH) * roomWidthCm;

  const top = !hasRoom
    ? num(table.top)
    : hasTopCm
      ? num(table.top_cm)
      : (num(table.top) / LEGACY_CANVAS_HEIGHT) * roomHeightCm;

  return {
    left,
    top,
    width,
    height,
    rotation: num(table.rotation),
    positioned: isPlacedFlag(table.isPlaced),
  };
}

// Tables the designer never placed used to be banished to a list beneath the
// plan. They are parked along the bottom wall instead, so they stay clickable
// and draggable and the plan remains the single view of the room.
function withFallbackPositions(tables, roomWidthCm, roomHeightCm) {
  const loose = tables.filter((table) => !table.geometry.positioned);
  if (!loose.length) return tables;

  const gap = Math.max(8, roomWidthCm * 0.01);
  const looseIds = new Set(loose.map((table) => table.id));
  const rowHeights = [0];
  const placement = new Map();
  let cursor = gap;
  let row = 0;

  loose.forEach((table) => {
    const { width, height } = table.geometry;

    if (cursor + width > roomWidthCm - gap && cursor > gap) {
      row += 1;
      rowHeights[row] = 0;
      cursor = gap;
    }

    rowHeights[row] = Math.max(rowHeights[row], height);
    placement.set(table.id, { left: cursor, row });
    cursor += width + gap;
  });

  // Stack the rows upward from the bottom wall.
  const totalHeight = rowHeights.reduce((sum, value) => sum + value + gap, 0);
  const rowTops = [];
  let runningTop = Math.max(gap, roomHeightCm - gap - totalHeight);

  for (let index = 0; index <= row; index += 1) {
    rowTops[index] = runningTop;
    runningTop += rowHeights[index] + gap;
  }

  return tables.map((table) => {
    if (!looseIds.has(table.id)) return table;

    const spot = placement.get(table.id);

    return {
      ...table,
      geometry: {
        ...table.geometry,
        left: clamp(
          spot.left,
          0,
          Math.max(0, roomWidthCm - table.geometry.width),
        ),
        top: clamp(
          rowTops[spot.row],
          0,
          Math.max(0, roomHeightCm - table.geometry.height),
        ),
      },
    };
  });
}

/* Memoised: the board re-renders on every selection, time change and status
   update, and redrawing thirty tables with their chairs and tags each time was
   the bulk of the cost. Every prop the board passes is now stable, so this only
   re-renders when the floor it draws actually changed. */
function ReservationFloorPlan({
  allTables = [],
  zoneName,
  availabilityTables = [],
  selectedTables = {},
  onSelectTable,
  layoutOverrides = [],
  layoutDraftItems = [],
  enableMove = false,
  onLayoutItemChange,
  room = null,
  objects = [],
  // Bookings that have not started yet, drawn where they will sit so a whole
  // sitting can be laid out without stepping the clock forward.
  upcomingTables = [],
  upcomingLayoutOverrides = [],
  centerCanvas = false,
  maxZoom = 1.8,
  children = null,
}) {
  const { t } = useTranslation();
  const [dragState, setDragState] = useState(null);
  const [snapGuide, setSnapGuide] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  /* The pinch listeners are native, so they need the element itself rather than
     a ref that was null on the render where the plan showed its empty state. */
  const [scrollElement, setScrollElement] = useState(null);
  const [liveDraftLayoutItems, setLiveDraftLayoutItems] = useState([]);

  const canvasRef = useRef(null);
  const scrollRef = useRef(null);
  /* A table is always draggable now, so a press has to decide for itself
     whether it was a click (select the booking) or a drag (move the table).
     Nothing moves until the pointer has travelled this far. */
  const dragMovedRef = useRef(false);
  const panRef = useRef(null);
  // Which axes are currently held by a snap, and where the table last stood
  // legally. Sliding resumes from there rather than from where the drag began,
  // which is what stopped it flinging the table back across the room.
  const stickySnapRef = useRef({ x: null, y: null, xGuide: null, yGuide: null });
  const lastValidRef = useRef(null);
  // table id -> the ids sharing a booking with it, so only its own group pulls.
  const groupMatesRef = useRef(new Map());
  // The parent only needs the final word each frame, not one per mouse event.
  const pendingLayoutItemRef = useRef(null);
  const layoutFlushFrameRef = useRef(null);
  // Where the scroll must land after a zoom, so the point under the cursor or
  // between two fingers stays put instead of the plan jumping to the middle.
  const pendingScrollRef = useRef(null);

  const attachScroll = useCallback((element) => {
    scrollRef.current = element;
    setScrollElement(element);
  }, []);
  // The pinch listeners are bound once and must not read a stale zoom.
  const zoomRef = useRef(1);
  const zoomAroundRef = useRef(() => {});
  const placedTablesRef = useRef([]);

  const zoomCeiling = Math.max(1, num(maxZoom, 1.8));

  const updateZoom = (nextZoom) => {
    const clamped = clamp(Number(nextZoom) || 1, 0.5, zoomCeiling);

    zoomRef.current = clamped;
    setZoom(clamped);
  };

  /**
   * Zoom while keeping one screen point anchored.
   *
   * Without this the plan slides out from under the fingers on every pinch,
   * because the scroll offset means something different at the new scale.
   */
  const zoomAround = (nextZoomRaw, clientX, clientY) => {
    const scroller = scrollRef.current;
    const current = zoomRef.current;
    const nextZoom = clamp(Number(nextZoomRaw) || 1, 0.5, zoomCeiling);

    // Publish immediately: a second button press in the same tick has to see
    // this one, or double-clicking the zoom only counts once.
    zoomRef.current = nextZoom;

    if (!scroller || nextZoom === current) {
      setZoom(nextZoom);
      return;
    }

    const rect = scroller.getBoundingClientRect();
    const offsetX = clientX - rect.left;
    const offsetY = clientY - rect.top;
    const ratio = nextZoom / current;

    pendingScrollRef.current = {
      left: (scroller.scrollLeft + offsetX) * ratio - offsetX,
      top: (scroller.scrollTop + offsetY) * ratio - offsetY,
    };

    setZoom(nextZoom);
  };

  // With no room record the plan falls back to the legacy 750x400 space
  // one-to-one, so callers that cannot supply a room render as they did before.
  const roomWidthCm = room
    ? positive(room.width, 10) * 100
    : LEGACY_CANVAS_WIDTH;
  const roomHeightCm = room
    ? positive(room.height, 6) * 100
    : LEGACY_CANVAS_HEIGHT;

  const legacyScale = room
    ? Math.min(
        LEGACY_CANVAS_WIDTH / roomWidthCm,
        LEGACY_CANVAS_HEIGHT / roomHeightCm,
      )
    : 1;

  const availabilityById = useMemo(
    () => buildAvailabilityMap(availabilityTables),
    [availabilityTables],
  );

  const layoutOverrideByTableId = useMemo(
    () => buildLayoutMap(layoutOverrides),
    [layoutOverrides],
  );

  const upcomingById = useMemo(
    () => buildAvailabilityMap(upcomingTables),
    [upcomingTables],
  );

  // A future booking keeps its own layout, so its tables are positioned from
  // that booking's override rather than the one being shown now.
  const upcomingOverrideByTableId = useMemo(
    () => buildLayoutMap(upcomingLayoutOverrides),
    [upcomingLayoutOverrides],
  );

  const layoutDraftByTableId = useMemo(() => {
    return buildLayoutMap([
      ...(layoutDraftItems || []),
      ...(liveDraftLayoutItems || []),
    ]);
  }, [layoutDraftItems, liveDraftLayoutItems]);

  const zoneRealTables = useMemo(() => {
    const availableIds = new Set(Array.from(availabilityById.keys()));

    return (allTables || [])
      .filter((table) => availableIds.has(String(table.id)))
      .map((table) => {
        const key = String(table.id);
        const isUpcoming =
          upcomingById.has(key) &&
          String(availabilityById.get(key)?.status ?? "0") !== "1";

        return {
          ...table,
          geometry: tableGeometryCm({
            table,
            // A table waiting on a future booking is drawn at that booking's
            // position, which is the whole point of showing it early.
            override: isUpcoming
              ? upcomingOverrideByTableId.get(key) ||
                layoutOverrideByTableId.get(key)
              : layoutOverrideByTableId.get(key),
            draft: layoutDraftByTableId.get(key),
            legacyScale,
            roomWidthCm,
            roomHeightCm,
            hasRoom: !!room,
          }),
        };
      })
      .sort((a, b) => Number(a.table_number) - Number(b.table_number));
  }, [
    allTables,
    availabilityById,
    upcomingById,
    upcomingOverrideByTableId,
    layoutOverrideByTableId,
    layoutDraftByTableId,
    legacyScale,
    roomWidthCm,
    roomHeightCm,
    room,
  ]);

  const placedTables = useMemo(
    () => withFallbackPositions(zoneRealTables, roomWidthCm, roomHeightCm),
    [zoneRealTables, roomWidthCm, roomHeightCm],
  );

  // For welding: which tables share a booking, and where each one sits. Two
  // tables only merge visually if the same reservation holds both — tables of
  // different bookings that happen to be adjacent stay separate.
  /**
   * Group the tables by the booking that holds them.
   *
   * This drives two things. Tables of one booking are welded together
   * visually, and the booking gets a single tag instead of one per table —
   * three tables of the same party used to stack three identical captions on
   * top of each other, which is most of the clutter on a busy plan.
   *
   * Bookings still to come are included, so a future party reads as one block
   * too.
   */
  const groups = useMemo(() => {
    const boundsById = new Map(
      placedTables.map((table) => [
        String(table.id),
        {
          left: table.geometry.left,
          top: table.geometry.top,
          right: table.geometry.left + table.geometry.width,
          bottom: table.geometry.top + table.geometry.height,
        },
      ]),
    );

    const tablesByReservation = new Map();

    const collect = (map) => {
      map.forEach((info, tableId) => {
        (info.reservationIds || []).forEach((reservationId) => {
          const key = String(reservationId);
          if (!tablesByReservation.has(key)) tablesByReservation.set(key, []);
          const list = tablesByReservation.get(key);
          if (!list.includes(String(tableId))) list.push(String(tableId));
        });
      });
    };

    collect(availabilityById);
    collect(upcomingById);

    const mates = new Map();
    // table id -> the whole group's bounds, for centring one tag under it
    const extent = new Map();
    // reservation id -> the table that carries the tag
    const tagOwner = new Map();

    tablesByReservation.forEach((tableIds, reservationId) => {
      const present = tableIds.filter((id) => boundsById.has(id));
      if (present.length < 2) return;

      const boxes = present.map((id) => boundsById.get(id));
      const groupBounds = {
        left: Math.min(...boxes.map((b) => b.left)),
        top: Math.min(...boxes.map((b) => b.top)),
        right: Math.max(...boxes.map((b) => b.right)),
        bottom: Math.max(...boxes.map((b) => b.bottom)),
      };

      // The lowest table carries the tag, so it hangs below the whole block
      // rather than through the middle of it.
      const owner = present.reduce((best, id) =>
        boundsById.get(id).bottom > boundsById.get(best).bottom ? id : best,
      );
      tagOwner.set(reservationId, owner);

      present.forEach((id) => {
        mates.set(
          id,
          present
            .filter((other) => other !== id)
            .map((other) => boundsById.get(other)),
        );
        extent.set(id, groupBounds);
      });
    });

    return { mates, extent, tagOwner };
  }, [placedTables, availabilityById, upcomingById]);

  useEffect(() => {
    const byReservation = new Map();

    const collect = (map) => {
      map.forEach((info, tableId) => {
        (info.reservationIds || []).forEach((reservationId) => {
          const key = String(reservationId);

          if (!byReservation.has(key)) byReservation.set(key, new Set());

          byReservation.get(key).add(String(tableId));
        });
      });
    };

    collect(availabilityById);
    collect(upcomingById);

    const mates = new Map();

    byReservation.forEach((ids) => {
      ids.forEach((id) => {
        if (!mates.has(id)) mates.set(id, new Set());

        ids.forEach((other) => {
          if (other !== id) mates.get(id).add(other);
        });
      });
    });

    groupMatesRef.current = mates;
  }, [availabilityById, upcomingById]);

  useEffect(() => {
    placedTablesRef.current = placedTables.map((table) => ({
      id: table.id,
      left: table.geometry.left,
      top: table.geometry.top,
      width: table.geometry.width,
      height: table.geometry.height,
      /* getBestSnapPosition skips anything not placed. This list left the flag
         out, so every candidate was skipped and proximity snapping never ran at
         all - the only thing that ever joined two tables was ramming one into
         the other hard enough to collide. */
      isPlaced: true,
    }));
  }, [placedTables]);

  /**
   * What a dragged table may not be put on top of.
   *
   * Walls, the kitchen, the bar, the stairs — anything solid — plus every other
   * table. Snapping alone was happy to drop a table straight through a wall or
   * onto its neighbour, which the automatic arrangement would never do.
   */
  const dragObstaclesRef = useRef([]);
  // The floor the drawn walls enclose. A drag may not leave it, whatever the
  // declared canvas size says.
  const interiorRef = useRef(null);

  useEffect(() => {
    interiorRef.current =
      roomWidthCm > 0 && roomHeightCm > 0
        ? interiorMask({ width: roomWidthCm, height: roomHeightCm }, objects)
        : null;
  }, [objects, roomWidthCm, roomHeightCm]);

  useEffect(() => {
    const solidElements = (objects || [])
      .filter(
        (item) => !["window", "plant"].includes(item.item_type),
      )
      .map((item) => {
        const width = positive(item.width, 100);
        const height = positive(item.height, 100);
        const radians = ((num(item.rotation) % 360) * Math.PI) / 180;
        const cos = Math.abs(Math.cos(radians));
        const sin = Math.abs(Math.sin(radians));
        const spanX = width * cos + height * sin;
        const spanY = width * sin + height * cos;
        const centerX = num(item.left) + width / 2;
        const centerY = num(item.top) + height / 2;

        return {
          left: centerX - spanX / 2,
          top: centerY - spanY / 2,
          right: centerX + spanX / 2,
          bottom: centerY + spanY / 2,
        };
      });

    dragObstaclesRef.current = solidElements;
  }, [objects]);

  useEffect(() => {
    if (!enableMove) {
      setLiveDraftLayoutItems([]);
      setDragState(null);
      setSnapGuide(null);
    }
  }, [enableMove]);

  /**
   * Drag the floor itself to pan.
   *
   * Only from the background - a press on a table moves that table - or from
   * the middle button, which pans from anywhere the way a drawing tool does.
   */
  const handlePanMouseDown = (event) => {
    const scroller = scrollRef.current;

    if (!scroller) return;
    if (event.button !== 0 && event.button !== 1) return;

    panRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startScrollLeft: scroller.scrollLeft,
      startScrollTop: scroller.scrollTop,
    };

    setIsPanning(true);
  };

  useEffect(() => {
    if (!isPanning) return undefined;

    const handleMove = (event) => {
      const pan = panRef.current;
      const scroller = scrollRef.current;

      if (!pan || !scroller) return;

      scroller.scrollLeft = pan.startScrollLeft - (event.clientX - pan.startX);
      scroller.scrollTop = pan.startScrollTop - (event.clientY - pan.startY);
    };

    const handleUp = () => {
      panRef.current = null;
      setIsPanning(false);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [isPanning]);

  const handleTableClick = (table) => {
    // A press that turned into a drag has already done its job.
    if (dragMovedRef.current) return;
    if (!table?.id || typeof onSelectTable !== "function") return;

    const availabilityInfo = availabilityById.get(String(table.id)) || {
      number: String(table.id),
      status: "0",
      reservationIds: [],
      reservationDetails: [],
    };

    onSelectTable(availabilityInfo, zoneName);
  };

  const handleMoveMouseDown = (event, table) => {
    if (!enableMove || !table?.id || !canvasRef.current) return;
    // Middle button pans, whatever is under it.
    if (event.button === 1) return;

    event.preventDefault();
    event.stopPropagation();

    dragMovedRef.current = false;
    stickySnapRef.current = { x: null, y: null, xGuide: null, yGuide: null };
    lastValidRef.current = {
      left: table.geometry.left,
      top: table.geometry.top,
    };

    setDragState({
      tableId: table.id,
      startMouseX: event.clientX,
      startMouseY: event.clientY,
      startLeft: table.geometry.left,
      startTop: table.geometry.top,
      width: table.geometry.width,
      height: table.geometry.height,
      rotation: table.geometry.rotation,
      // clientWidth/Height is the padding box, which is what the tables'
      // percentage offsets resolve against. The border box would make every
      // drag drift by the width of the canvas border.
      canvasWidth: canvasRef.current.clientWidth,
      canvasHeight: canvasRef.current.clientHeight,
    });
  };

  const zoomAroundCentre = (nextZoom) => {
    const scroller = scrollRef.current;

    if (!scroller) {
      updateZoom(nextZoom);
      return;
    }

    const rect = scroller.getBoundingClientRect();

    zoomAround(nextZoom, rect.left + rect.width / 2, rect.top + rect.height / 2);
  };

  zoomAroundRef.current = zoomAround;

  useLayoutEffect(() => {
    const pending = pendingScrollRef.current;

    if (!pending) return;

    pendingScrollRef.current = null;

    const scroller = scrollRef.current;
    if (!scroller) return;

    scroller.scrollLeft = pending.left;
    scroller.scrollTop = pending.top;
  }, [zoom]);

  /* Pinch to zoom, and trackpad pinch on a desktop, which the browser reports
     as a wheel event with ctrlKey set. Both need non-passive listeners to stop
     the page zooming instead, which React's own handlers cannot promise. */
  useEffect(() => {
    const scroller = scrollElement;

    if (!scroller) return undefined;

    let pinchStartDistance = 0;
    let pinchStartZoom = 1;

    const distanceOf = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;

      return Math.hypot(dx, dy);
    };

    const midpointOf = (touches) => ({
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    });

    const handleTouchStart = (event) => {
      if (event.touches.length !== 2) return;

      pinchStartDistance = distanceOf(event.touches);
      pinchStartZoom = zoomRef.current;
    };

    const handleTouchMove = (event) => {
      if (event.touches.length !== 2 || !pinchStartDistance) return;

      event.preventDefault();

      const distance = distanceOf(event.touches);
      const midpoint = midpointOf(event.touches);

      zoomAroundRef.current(
        pinchStartZoom * (distance / pinchStartDistance),
        midpoint.x,
        midpoint.y,
      );
    };

    const handleTouchEnd = (event) => {
      if (event.touches.length < 2) pinchStartDistance = 0;
    };

    const handleWheel = (event) => {
      if (!event.ctrlKey) return;

      event.preventDefault();

      // Trackpads report fine-grained deltas; scale them into a gentle factor.
      const factor = Math.exp(-event.deltaY / 220);

      zoomAroundRef.current(
        zoomRef.current * factor,
        event.clientX,
        event.clientY,
      );
    };

    scroller.addEventListener("touchstart", handleTouchStart, { passive: true });
    scroller.addEventListener("touchmove", handleTouchMove, { passive: false });
    scroller.addEventListener("touchend", handleTouchEnd, { passive: true });
    scroller.addEventListener("touchcancel", handleTouchEnd, { passive: true });
    scroller.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      scroller.removeEventListener("touchstart", handleTouchStart);
      scroller.removeEventListener("touchmove", handleTouchMove);
      scroller.removeEventListener("touchend", handleTouchEnd);
      scroller.removeEventListener("touchcancel", handleTouchEnd);
      scroller.removeEventListener("wheel", handleWheel);
    };
  }, [scrollElement]);

  useEffect(() => {
    if (!dragState) return undefined;

    const handleMouseMove = (event) => {
      if (!dragMovedRef.current) {
        const travelled = Math.hypot(
          event.clientX - dragState.startMouseX,
          event.clientY - dragState.startMouseY,
        );

        // Still within the slop: this may yet turn out to be a click.
        if (travelled < DRAG_THRESHOLD_PX) return;

        dragMovedRef.current = true;
      }

      const dx =
        ((event.clientX - dragState.startMouseX) / dragState.canvasWidth) *
        roomWidthCm;
      const dy =
        ((event.clientY - dragState.startMouseY) / dragState.canvasHeight) *
        roomHeightCm;

      const maxLeft = Math.max(0, roomWidthCm - dragState.width);
      const maxTop = Math.max(0, roomHeightCm - dragState.height);

      const rawLeft = clamp(dragState.startLeft + dx, 0, maxLeft);
      const rawTop = clamp(dragState.startTop + dy, 0, maxTop);

      const snapResult = getBestSnapPosition({
        tableId: dragState.tableId,
        proposedLeft: rawLeft,
        proposedTop: rawTop,
        width: dragState.width,
        height: dragState.height,
        otherTables: placedTablesRef.current,
        sticky: stickySnapRef.current,
        mateIds:
          groupMatesRef.current.get(String(dragState.tableId)) || NO_MATES,
      });

      let nextLeft = clamp(snapResult.left, 0, maxLeft);
      let nextTop = clamp(snapResult.top, 0, maxTop);

      // Refuse a position that lands on a wall, a fitting, or another table.
      // Tried snapped-first, then the raw pointer position, then each axis on
      // its own so a table can still slide along an obstacle instead of
      // sticking the moment it touches one.
      const collides = (left, top) => {
        const box = {
          left,
          top,
          right: left + dragState.width,
          bottom: top + dragState.height,
        };

        // Outside the walls is not floor, so it is not a place to stand a
        // table — regardless of how much bare canvas is out there.
        if (
          !withinInterior(interiorRef.current, {
            ...box,
            centerX: left + dragState.width / 2,
            centerY: top + dragState.height / 2,
          })
        ) {
          return true;
        }

        const hitsSolid = dragObstaclesRef.current.some(
          (obstacle) =>
            box.left < obstacle.right + MIN_OBSTACLE_GAP_CM &&
            obstacle.left < box.right + MIN_OBSTACLE_GAP_CM &&
            box.top < obstacle.bottom + MIN_OBSTACLE_GAP_CM &&
            obstacle.top < box.bottom + MIN_OBSTACLE_GAP_CM,
        );
        if (hitsSolid) return true;

        return placedTablesRef.current.some((other) => {
          if (Number(other.id) === Number(dragState.tableId)) return false;
          return (
            box.left < other.left + other.width &&
            other.left < box.right &&
            box.top < other.top + other.height &&
            other.top < box.bottom
          );
        });
      };

      const last = lastValidRef.current || {
        left: dragState.startLeft,
        top: dragState.startTop,
      };

      /* Where the table may actually go, best first.
       *
       * The last two let it slide along whatever it has run into, and they
       * slide from where it is standing now - not from where the drag started.
       * Using the start position is what made a blocked drag fling the table
       * back across the room. */
      // Where the hand is actually asking for, snapped first then raw.
      const directCandidates = [
        [nextLeft, nextTop],
        [clamp(rawLeft, 0, maxLeft), clamp(rawTop, 0, maxTop)],
      ];

      // Last resort: keep one axis where it stands and move only the other, so
      // a table slides along whatever it is pressed against.
      const slideCandidates = [
        [nextLeft, last.top],
        [last.left, nextTop],
      ];

      const firstFree = (list) => list.find(([left, top]) => !collides(left, top));

      /**
       * Walk from where the table stands towards a target, stopping at the last
       * clear step.
       *
       * A single mouse event can carry the pointer further than a table is
       * wide - a quick flick, or one dropped frame. Testing only the end point
       * let the table tunnel clean through a neighbour and reappear on the far
       * side, which is exactly what teleporting looks like.
       */
      const sweepTowards = (targetLeft, targetTop) => {
        const totalX = targetLeft - last.left;
        const totalY = targetTop - last.top;
        const distance = Math.hypot(totalX, totalY);
        const stepSize = Math.max(
          4,
          Math.min(dragState.width, dragState.height) / 3,
        );
        const steps = Math.ceil(distance / stepSize);

        if (steps <= 1) {
          return collides(targetLeft, targetTop) ? null : [targetLeft, targetTop];
        }

        let reachedRatio = 0;
        let blockedRatio = null;

        for (let step = 1; step <= steps; step += 1) {
          const ratio = step / steps;

          if (collides(last.left + totalX * ratio, last.top + totalY * ratio)) {
            blockedRatio = ratio;
            break;
          }

          reachedRatio = ratio;
        }

        /* Close the remaining gap. A step is a third of the table, so stopping
           at the last clear one could leave the table a hand's width short of
           what it ran into.
           
           This runs even when the very first step was blocked. It used to give
           up there, which meant that within a step's reach of anything the
           table simply refused to budge - so a join could only be made from one
           exact spot. Now it always creeps as close as it legally can. */
        if (blockedRatio !== null) {
          let low = reachedRatio;
          let high = blockedRatio;

          for (let i = 0; i < 7; i += 1) {
            const mid = (low + high) / 2;

            if (collides(last.left + totalX * mid, last.top + totalY * mid)) {
              high = mid;
            } else {
              low = mid;
            }
          }

          reachedRatio = low;
        }

        // Nowhere to go at all: less than a millimetre of the way there.
        if (reachedRatio * Math.hypot(totalX, totalY) < 0.1) return null;

        return [
          last.left + totalX * reachedRatio,
          last.top + totalY * reachedRatio,
        ];
      };

      let allowed = null;

      for (const [targetLeft, targetTop] of directCandidates) {
        const swept = sweepTowards(targetLeft, targetTop);

        if (!swept) continue;

        const arrived =
          Math.abs(swept[0] - targetLeft) < 0.01 &&
          Math.abs(swept[1] - targetTop) < 0.01;

        if (arrived) {
          allowed = swept;
          break;
        }

        /* Stopped short: stay at the last clear step, which the refinement
         * inside the sweep has already pressed right up against whatever
         * blocked it.
         *
         * There used to be a pass here that hunted for a flush position against
         * any aligned obstacle. Walls span the whole room, so "aligned" matched
         * one from clear across the floor and the table was flung over to it -
         * the teleport. Every position now comes from sweeping outward from
         * where the table stands, so it can only ever end up somewhere the hand
         * actually took it. */
        allowed = swept;
        break;
      }

      if (!allowed) {
        allowed = slideCandidates
          .map(([left, top]) => sweepTowards(left, top))
          .find(Boolean);
      }

      // Boxed in on every axis: hold position rather than overlap something.
      if (!allowed) return;

      [nextLeft, nextTop] = allowed;

      lastValidRef.current = { left: nextLeft, top: nextTop };

      // Remember a snap only if it actually survived the collision test, so the
      // hand is never held to an alignment the table could not take.
      stickySnapRef.current = {
        x: nextLeft === snapResult.snappedX ? snapResult.snappedX : null,
        y: nextTop === snapResult.snappedY ? snapResult.snappedY : null,
        xGuide: snapResult.guideX,
        yGuide: snapResult.guideY,
      };

      const activeGuide =
        (stickySnapRef.current.x ? snapResult.guideX : null) ||
        (stickySnapRef.current.y ? snapResult.guideY : null);

      setSnapGuide(
        activeGuide
          ? {
              left: (activeGuide.left / roomWidthCm) * 100,
              top: (activeGuide.top / roomHeightCm) * 100,
              width: (activeGuide.width / roomWidthCm) * 100,
              height: (activeGuide.height / roomHeightCm) * 100,
            }
          : null,
      );

      // Overrides live in the legacy pixel space, so the emitted item stays in
      // that space and the existing save path is untouched.
      const nextLayoutItem = {
        table_id: dragState.tableId,
        // Legacy pixels for the older reservation views...
        left: Math.round(nextLeft * legacyScale),
        top: Math.round(nextTop * legacyScale),
        width: Math.max(1, Math.round(dragState.width * legacyScale)),
        height: Math.max(1, Math.round(dragState.height * legacyScale)),
        // ...and the exact centimetres this plan actually draws from.
        left_cm: Math.round(nextLeft * 100) / 100,
        top_cm: Math.round(nextTop * 100) / 100,
        width_cm: Math.round(dragState.width * 100) / 100,
        height_cm: Math.round(dragState.height * 100) / 100,
        isPlaced: true,
        rotation: Math.round(dragState.rotation || 0),
      };

      // The plan draws from its own live draft, so this is immediate.
      setLiveDraftLayoutItems((previousItems) =>
        mergeLayoutDraftItem(previousItems, nextLayoutItem),
      );

      /* The board above only needs the latest word each frame. Calling it on
         every mouse event re-rendered the whole board dozens of times a second,
         which is what made a smooth hand movement look like stuttering. */
      pendingLayoutItemRef.current = nextLayoutItem;

      if (layoutFlushFrameRef.current === null) {
        layoutFlushFrameRef.current = requestAnimationFrame(() => {
          layoutFlushFrameRef.current = null;

          const item = pendingLayoutItemRef.current;
          pendingLayoutItemRef.current = null;

          if (item && typeof onLayoutItemChange === "function") {
            onLayoutItemChange(item);
          }
        });
      }
    };

    const handlePointerUp = () => {
      // Make sure the last position reaches the board even if the drag ended
      // between frames.
      if (layoutFlushFrameRef.current !== null) {
        cancelAnimationFrame(layoutFlushFrameRef.current);
        layoutFlushFrameRef.current = null;
      }

      const item = pendingLayoutItemRef.current;
      pendingLayoutItemRef.current = null;

      if (item && typeof onLayoutItemChange === "function") {
        onLayoutItemChange(item);
      }

      stickySnapRef.current = { x: null, y: null, xGuide: null, yGuide: null };

      setDragState(null);
      setSnapGuide(null);
      // The live draft stays visible until the parent's draft catches up; it
      // clears on leaving move mode or on save/discard.
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handlePointerUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handlePointerUp);
    };
  }, [dragState, onLayoutItemChange, roomWidthCm, roomHeightCm, legacyScale]);

  if (!zoneName) {
    return <EmptyState>{t("ui.i18nCleanup.selectRoomToViewTables")}</EmptyState>;
  }

  if (!zoneRealTables.length) {
    return (
      <EmptyState>{t("ui.i18nCleanup.noTablesAvailableInRoom")}</EmptyState>
    );
  }

  const zoomFillPercent = clamp(
    ((zoom - 0.5) / Math.max(0.01, zoomCeiling - 0.5)) * 100,
    0,
    100,
  );

  const roomWidthM = roomWidthCm / 100;
  const roomHeightM = roomHeightCm / 100;
  const planOpacity = clamp(num(room?.floor_plan_opacity, 0.35), 0.05, 1);
  const planImage = resolvePlanImage(room?.floor_plan_image);

  return (
    <PlanFrame>
      <PlanScroll
        ref={attachScroll}
        $center={centerCanvas}
        $panning={isPanning}
        onMouseDown={handlePanMouseDown}
      >
        <ZoomSurface $zoom={zoom} $center={centerCanvas}>
          <PlanCanvas
            ref={canvasRef}
            $ratio={`${roomWidthM} / ${roomHeightM}`}
            $gridX={100 / Math.max(1, roomWidthM)}
            $gridY={100 / Math.max(1, roomHeightM)}
          >
            {planImage && (
              <FloorPlanImage src={planImage} alt="" $opacity={planOpacity} />
            )}

            {(objects || []).map((item) => {
              const width = positive(item.width, 100);
              const height = positive(item.height, 100);
              const showCaption =
                !SLAB_TYPES.has(item.item_type) &&
                (width / roomWidthCm) * 100 >= CAPTION_MIN_PERCENT &&
                (height / roomHeightCm) * 100 >= CAPTION_MIN_PERCENT;

              return (
                <ObjectShape
                  key={`object-${item.id}`}
                  $type={item.item_type}
                  $color={item.color}
                  $opacity={clamp(num(item.opacity, 1), 0.1, 1)}
                  $left={(num(item.left) / roomWidthCm) * 100}
                  $top={(num(item.top) / roomHeightCm) * 100}
                  $width={(width / roomWidthCm) * 100}
                  $height={(height / roomHeightCm) * 100}
                  $rotation={num(item.rotation)}
                  aria-hidden="true"
                >
                  {showCaption && (
                    <>
                      <span>
                        {OBJECT_ICONS[item.item_type] || OBJECT_ICONS.custom}
                      </span>
                      <span>{item.items_name}</span>
                    </>
                  )}
                </ObjectShape>
              );
            })}

            {snapGuide && (
              <SnapGuide
                $left={snapGuide.left}
                $top={snapGuide.top}
                $width={snapGuide.width}
                $height={snapGuide.height}
              />
            )}

            {placedTables.map((table) => {
              const availabilityInfo =
                availabilityById.get(String(table.id)) || {};
              const selected = isTableSelected(
                selectedTables,
                zoneName,
                table.id,
              );
              const occupied = String(availabilityInfo.status ?? "0") === "1";
              const upcomingInfo = !occupied
                ? upcomingById.get(String(table.id))
                : null;
              const state = selected
                ? "selected"
                : occupied
                  ? "occupied"
                  : upcomingInfo
                    ? "upcoming"
                    : "free";

              const { left, top, width, height, rotation } = table.geometry;
              const widthPercent = (width / roomWidthCm) * 100;
              const heightPercent = (height / roomHeightCm) * 100;
              const showLabel =
                widthPercent >= LABEL_MIN_PERCENT &&
                heightPercent >= LABEL_MIN_PERCENT;

              // A future booking has no current occupancy to describe, so its
              // own details drive the tag.
              const bookings =
                (upcomingInfo
                  ? upcomingInfo.reservationDetails
                  : availabilityInfo.reservationDetails) || [];
              const booking = bookings[0];

              const tableKey = String(table.id);
              const joined = joinedSides(
                {
                  left,
                  top,
                  right: left + width,
                  bottom: top + height,
                },
                groups.mates.get(tableKey) || [],
              );

              // One tag for the whole party, hung under the block and centred
              // on it, rather than an identical caption on every table.
              const groupExtent = groups.extent.get(tableKey);
              const ownsTag =
                !booking ||
                !groupExtent ||
                groups.tagOwner.get(String(booking.id)) === tableKey;
              const tagShiftPercent = groupExtent
                ? ((groupExtent.left + groupExtent.right) / 2 -
                    (left + width / 2)) /
                  width *
                  100
                : 0;
              const welded =
                joined.top || joined.bottom || joined.left || joined.right;

              return (
                <TableNode
                  type="button"
                  key={table.id}
                  $left={(left / roomWidthCm) * 100}
                  $top={(top / roomHeightCm) * 100}
                  $width={widthPercent}
                  $height={heightPercent}
                  $rotation={rotation}
                  $movable={enableMove}
                  $dragging={
                    !!dragState &&
                    Number(dragState.tableId) === Number(table.id)
                  }
                  onMouseDown={(event) => handleMoveMouseDown(event, table)}
                  onClick={() => handleTableClick(table)}
                  title={[
                    t("ui.i18nFinalCleanup.tableLabel", {
                      label: getTableLabel(table),
                    }),
                    enableMove
                      ? t("ui.i18nFinalCleanup.dragToAdjustPosition")
                      : "",
                    getReservationInfoText(availabilityInfo, t),
                  ]
                    .filter(Boolean)
                    .join(" — ")}
                >
                  <TableBody>
                    {chairPositions(table.number_of_seats, joined).map(
                      (chair, index) => (
                        <Chair
                          key={`${table.id}-chair-${index}`}
                          $left={chair.left}
                          $top={chair.top}
                          $angle={chair.angle}
                          $state={state}
                        />
                      ),
                    )}

                    <TableSurface
                      $round={
                        // A welded table is part of a run, so it is drawn
                        // square whatever its own shape: a round top cannot
                        // meet a neighbour flush.
                        !welded &&
                        String(table.round_or_square).toLowerCase() === "round"
                      }
                      $joined={joined}
                      $state={state}
                    >
                      {showLabel ? getTableLabel(table) : ""}
                    </TableSurface>
                  </TableBody>

                  {booking && showLabel && ownsTag && (
                    <BookingTag
                      $selected={selected}
                      $upcoming={!!upcomingInfo}
                      $shift={tagShiftPercent}
                    >
                      {/* For a booking still to come the time is the point, so
                          it leads; for one seated now the name does. */}
                      {upcomingInfo ? (
                        <>
                          <b>{booking.time}</b>
                          <i>
                            {booking.full_name ||
                              t("ui.i18nFinalCleanup.reservation")}
                            {booking.number_of_people
                              ? ` · ${booking.number_of_people}p`
                              : ""}
                          </i>
                        </>
                      ) : (
                        <>
                          <b>
                            {booking.full_name ||
                              t("ui.i18nFinalCleanup.reservation")}
                          </b>
                          <i>
                            {booking.time}
                            {booking.number_of_people
                              ? ` · ${booking.number_of_people}p`
                              : ""}
                            {bookings.length > 1
                              ? ` +${bookings.length - 1}`
                              : ""}
                          </i>
                        </>
                      )}
                    </BookingTag>
                  )}
                </TableNode>
              );
            })}
          </PlanCanvas>
        </ZoomSurface>
      </PlanScroll>

      <PlanFooter>
        <FooterSlot>{children}</FooterSlot>

        <ZoomControls>
          <ZoomButton
            type="button"
            disabled={zoom <= 0.5}
            onClick={() => zoomAroundCentre(zoomRef.current - ZOOM_STEP)}
            aria-label={t("floorPlan.zoomOut", { defaultValue: "Reduzir" })}
            title={t("floorPlan.zoomOut", { defaultValue: "Reduzir" })}
          >
            <ZoomGlyph>−</ZoomGlyph>
          </ZoomButton>

          <ZoomTrack>
            <ZoomFill style={{ width: `${zoomFillPercent}%` }} />
            <ZoomRange
              type="range"
              min="50"
              max={Math.round(zoomCeiling * 100)}
              step="1"
              value={Math.round(zoom * 100)}
              onChange={(event) =>
                zoomAroundCentre(Number(event.target.value) / 100)
              }
              aria-label={t("floorPlan.zoom", { defaultValue: "Zoom" })}
            />
          </ZoomTrack>

          <ZoomButton
            type="button"
            disabled={zoom >= zoomCeiling}
            onClick={() => zoomAroundCentre(zoomRef.current + ZOOM_STEP)}
            aria-label={t("floorPlan.zoomIn", { defaultValue: "Ampliar" })}
            title={t("floorPlan.zoomIn", { defaultValue: "Ampliar" })}
          >
            <ZoomGlyph>+</ZoomGlyph>
          </ZoomButton>

          {/* Tapping the readout goes back to 1:1 - the one zoom level anyone
              ever wants to return to. */}
          <ZoomPercent
            type="button"
            onClick={() => zoomAroundCentre(1)}
            title={t("floorPlan.zoomReset", { defaultValue: "Voltar a 100%" })}
          >
            {Math.round(zoom * 100)}%
          </ZoomPercent>
        </ZoomControls>
      </PlanFooter>
    </PlanFrame>
  );
}

export default React.memo(ReservationFloorPlan);
